/**
 * Product events (where new players drop off): `track('quickplay_click')`. Batched to the
 * server's `POST /api/t` every few seconds, and flushed with `sendBeacon` when the page is
 * hidden or closed, so the last moment of a visit (`match_leave`) still arrives.
 *
 * A visitor is a random id kept in this browser (`sa.vid`; a fresh one per page where storage is
 * blocked), a visit is one page load. Nothing here identifies a person: the server adds the
 * signed-in user id (from the session cookie), the country (Cloudflare) and the user agent.
 * Sent as text/plain so a beacon to a cross-origin dev server needs no preflight.
 */
const BASE = (import.meta.env.VITE_SERVER_URL as string | undefined)?.replace(/\/$/, '') ?? '';
const URL_T = `${BASE}/api/t`;
/** send at most this often (ms), or at once from `MAX_QUEUE` events */
const FLUSH_MS = 5000;
const MAX_QUEUE = 20;
/** client errors: at most this many per page (a broken frame loop would flood) */
const MAX_ERRORS = 5;

export type Props = Record<string, string | number | boolean | null | undefined>;

interface Ev {
  e: string;
  /** ms since the page loaded */
  t: number;
  p?: Props;
}

function randomId(): string {
  const b = crypto.getRandomValues(new Uint8Array(12));
  return Array.from(b, (x) => x.toString(36).padStart(2, '0')).join('').slice(0, 20);
}

function visitorId(): string {
  try {
    const v = localStorage.getItem('sa.vid');
    if (v && /^[a-z0-9]{8,40}$/.test(v)) return v;
    const n = randomId();
    localStorage.setItem('sa.vid', n);
    return n;
  } catch {
    return randomId();
  }
}

class Tracker {
  private readonly vid = visitorId();
  private readonly sid = randomId();
  private queue: Ev[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private errors = 0;

  constructor() {
    // closing the page: last words first (`onLeave`), then everything goes out by beacon
    addEventListener('pagehide', () => {
      for (const fn of leaveHooks) fn();
      this.flush(true);
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flush(true);
    });
    addEventListener('error', (e) => this.error(e.message, e.filename, e.lineno));
    addEventListener('unhandledrejection', (e) => this.error(String((e.reason as Error | undefined)?.message ?? e.reason), 'promise', 0));
  }

  track(e: string, p?: Props): void {
    this.queue.push({ e, t: Math.round(performance.now()), ...(p ? { p } : {}) });
    if (this.queue.length >= MAX_QUEUE) this.flush(false);
    else this.timer ??= setTimeout(() => this.flush(false), FLUSH_MS);
  }

  private error(msg: string, src: string | undefined, line: number | undefined): void {
    if (this.errors++ >= MAX_ERRORS) return;
    this.track('client_error', { msg: msg.slice(0, 300), src: (src ?? '').slice(-120), line: line ?? 0 });
  }

  /** `leaving`: the page may be gone before a fetch answers — a beacon instead. */
  private flush(leaving: boolean): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    if (this.queue.length === 0) return;
    const body = JSON.stringify({ vid: this.vid, sid: this.sid, ev: this.queue });
    this.queue = [];
    const blob = new Blob([body], { type: 'text/plain' });
    if (leaving && navigator.sendBeacon?.(URL_T, blob)) return;
    void fetch(URL_T, { method: 'POST', body: blob, credentials: 'include', keepalive: true }).catch(() => {});
  }
}

let tracker: Tracker | null = null;
const leaveHooks = new Set<() => void>();

const inBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

/** Record a product event (no-op outside a browser, e.g. unit tests). */
export function track(e: string, p?: Props): void {
  if (!inBrowser()) return;
  tracker ??= new Tracker();
  tracker.track(e, p);
}

/** `fn` runs as the page closes, before the last flush (its `track` calls still go out). Returns: unregister. */
export function onLeave(fn: () => void): () => void {
  if (!inBrowser()) return () => {};
  tracker ??= new Tracker();
  leaveHooks.add(fn);
  return () => leaveHooks.delete(fn);
}
